import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { vendorRegistrationService } from '../../services/vendorRegistration.service';
import localeConfig from '../../config/locale.config';

const VendorServiceRatesForm = ({ data, onNext, onPrevious, onSave, loading, colors, isPending, registrationStatus }) => {
    const [serviceRates, setServiceRates] = useState([]);
    const [expandedRates, setExpandedRates] = useState(new Set([])); // Keep first rate collapsed by default
    const [serviceTypes, setServiceTypes] = useState([]);
    const [serviceSubtypes, setServiceSubtypes] = useState({});
    const [loadingServiceTypes, setLoadingServiceTypes] = useState(false);

    useEffect(() => {
        loadServiceTypes();
    }, []);

    useEffect(() => {
        if (data.serviceRates && data.serviceRates.length > 0) {
            setServiceRates(data.serviceRates);
            // Keep first rate collapsed by default
            setExpandedRates(new Set([]));
            
            // Load subtypes for any pre-selected service types
            data.serviceRates.forEach(rate => {
                if (rate.service_type_code) {
                    loadServiceSubtypes(rate.service_type_code);
                }
            });
        } else {
            // Add at least one service rate with proper structure
            setServiceRates([{
                service_type_code: '',
                service_subtype_code: '',
                time_slot: 'DAY',
                time_slot_start: '06:00:00',
                time_slot_end: '18:00:00',
                base_service_rate: '',
                base_max_kms: '',
                additional_rate_per_km: '',
                fixed_rate: '',
                cancel_charges_per_km_upto_base_kms: '',
                cancel_charges_per_km_above_base_kms: '',
                effective_from: new Date().toISOString().split('T')[0],
                effective_to: ''
            }]);
            // Keep first rate collapsed by default
            setExpandedRates(new Set([]));
        }
    }, [data.serviceRates]);

    const loadServiceTypes = async () => {
        try {
            setLoadingServiceTypes(true);
            const types = await vendorRegistrationService.getAllServiceTypes();
            setServiceTypes(types);
        } catch (error) {
            console.error('Error loading service types:', error);
        } finally {
            setLoadingServiceTypes(false);
        }
    };

    const loadServiceSubtypes = async (serviceTypeCode) => {
        if (!serviceTypeCode) {
            setServiceSubtypes({});
            return;
        }

        try {
            const subtypes = await vendorRegistrationService.getServiceSubtypes(serviceTypeCode);
            setServiceSubtypes(prev => ({
                ...prev,
                [serviceTypeCode]: subtypes
            }));
        } catch (error) {
            console.error('Error loading service subtypes:', error);
        }
    };

    const toggleRateExpansion = (index) => {
        const newExpanded = new Set(expandedRates);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedRates(newExpanded);
    };

    const handleServiceRateChange = (index, field, value) => {
        const updatedRates = [...serviceRates];
        updatedRates[index] = {
            ...updatedRates[index],
            [field]: value
        };
        setServiceRates(updatedRates);

        // If service type changed, load subtypes
        if (field === 'service_type_code') {
            loadServiceSubtypes(value);
            // Clear subtype when service type changes
            updatedRates[index].service_subtype_code = '';
            setServiceRates(updatedRates);
        }
    };

    const addServiceRate = () => {
        const newIndex = serviceRates.length;
        setServiceRates([...serviceRates, {
            service_type_code: '',
            service_subtype_code: '',
            time_slot: 'DAY',
            time_slot_start: '06:00:00',
            time_slot_end: '18:00:00',
            base_service_rate: '',
            base_max_kms: '',
            additional_rate_per_km: '',
            fixed_rate: '',
            cancel_charges_per_km_upto_base_kms: '',
            cancel_charges_per_km_above_base_kms: '',
            effective_from: new Date().toISOString().split('T')[0],
            effective_to: ''
        }]);
        // Auto-expand the new rate
        setExpandedRates(new Set([...expandedRates, newIndex]));
    };

    const removeServiceRate = (index) => {
        if (serviceRates.length > 1) {
            const updatedRates = serviceRates.filter((_, i) => i !== index);
            setServiceRates(updatedRates);
            
            // Update expanded rates
            const newExpanded = new Set();
            expandedRates.forEach(expandedIndex => {
                if (expandedIndex < index) {
                    newExpanded.add(expandedIndex);
                } else if (expandedIndex > index) {
                    newExpanded.add(expandedIndex - 1);
                }
            });
            setExpandedRates(newExpanded);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Prevent submission if registration is pending
        if (isPending) {
            return;
        }
        
        // Basic validation
        const hasValidRate = serviceRates.some(rate => 
            rate.service_type_code && 
            rate.base_service_rate && 
            rate.base_max_kms && 
            rate.additional_rate_per_km
        );
        
        if (!hasValidRate) {
            alert('Please fill in all required fields for at least one service rate');
            return;
        }

        onNext({ serviceRates });
    };

    const handleSave = () => {
        // Prevent saving if registration is pending
        if (isPending) {
            onSave({});
            return;
        }

        // Basic validation for save
        const hasValidRate = serviceRates.some(rate => 
            rate.service_type_code && 
            rate.base_service_rate && 
            rate.base_max_kms && 
            rate.additional_rate_per_km
        );
        
        if (!hasValidRate) {
            alert('Please fill in all required fields for at least one service rate before saving');
            return;
        }

        onSave({ serviceRates });
    };

    const getRateSummary = (rate) => {
        const serviceType = serviceTypes.find(st => st.service_type_code === rate.service_type_code);
        const serviceTypeName = serviceType ? serviceType.service_type_name : 'Unknown Service';
        
        const subtype = serviceSubtypes[rate.service_type_code]?.find(sst => sst.subtype_code === rate.service_subtype_code);
        const subtypeName = subtype ? subtype.subtype_name : '';
        
        const baseRate = rate.base_service_rate ? `${localeConfig.currency.symbol}${rate.base_service_rate}` : 'No rate';
        const timeSlot = rate.time_slot || 'DAY';
        
        return `${serviceTypeName}${subtypeName ? ` - ${subtypeName}` : ''} (${timeSlot}) - ${baseRate}`;
    };

    const timeSlots = [
        { value: 'DAY', label: 'Day (6 AM - 6 PM)' },
        { value: 'NIGHT', label: 'Night (6 PM - 6 AM)' }
    ];

    return (
        <div>
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">Service Rates</h2>
                <p className="text-sm text-gray-600">Define the rates for different services you provide.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Service Rate Cards */}
                <div className="space-y-3">
                    {serviceRates.map((rate, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                            {/* Rate Header */}
                            <div 
                                className="bg-blue-50 px-4 py-2.5 cursor-pointer hover:bg-blue-100 transition-colors border-b border-blue-100"
                                onClick={() => toggleRateExpansion(index)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        {expandedRates.has(index) ? (
                                            <ChevronDown className="w-4 h-4 text-gray-500" />
                                        ) : (
                                            <ChevronRight className="w-4 h-4 text-gray-500" />
                                        )}
                                        <div className="flex items-center space-x-3">
                                            <h3 className="text-base font-medium text-gray-900">
                                                Service Rate {index + 1}
                                            </h3>
                                            <span className="text-xs text-gray-500">
                                                {rate.time_slot || 'DAY'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-xs text-gray-600 max-w-xs truncate">
                                            {getRateSummary(rate)}
                                        </span>
                                        {serviceRates.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeServiceRate(index);
                                                }}
                                                className="text-red-600 hover:text-red-800 p-1"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Rate Content */}
                            {expandedRates.has(index) && (
                                <div className="p-4 bg-white">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Service Type */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Service Type *
                                            </label>
                                            <select
                                                value={rate.service_type_code}
                                                onChange={(e) => handleServiceRateChange(index, 'service_type_code', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                disabled={loadingServiceTypes}
                                                required
                                            >
                                                <option value="">Select Service Type</option>
                                                {serviceTypes.map(type => (
                                                    <option key={type.service_type_code} value={type.service_type_code}>
                                                        {type.service_type_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Service Subtype */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Service Subtype
                                            </label>
                                            <select
                                                value={rate.service_subtype_code}
                                                onChange={(e) => handleServiceRateChange(index, 'service_subtype_code', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                disabled={!rate.service_type_code}
                                            >
                                                <option value="">Select Subtype (Optional)</option>
                                                {serviceSubtypes[rate.service_type_code]?.map(subtype => (
                                                    <option key={subtype.subtype_code} value={subtype.subtype_code}>
                                                        {subtype.subtype_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Time Slot */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Time Slot *
                                            </label>
                                            <select
                                                value={rate.time_slot}
                                                onChange={(e) => handleServiceRateChange(index, 'time_slot', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            >
                                                {timeSlots.map(slot => (
                                                    <option key={slot.value} value={slot.value}>
                                                        {slot.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Time Range */}
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Start Time *
                                                </label>
                                                <input
                                                    type="time"
                                                    value={rate.time_slot_start}
                                                    onChange={(e) => handleServiceRateChange(index, 'time_slot_start', e.target.value)}
                                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    End Time *
                                                </label>
                                                <input
                                                    type="time"
                                                    value={rate.time_slot_end}
                                                    onChange={(e) => handleServiceRateChange(index, 'time_slot_end', e.target.value)}
                                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {/* Base Service Rate */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Base Service Rate ({localeConfig.currency.symbol}) *
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={rate.base_service_rate}
                                                onChange={(e) => handleServiceRateChange(index, 'base_service_rate', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="0.00"
                                                required
                                            />
                                        </div>

                                        {/* Base Max KMs */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Base Max KMs *
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={rate.base_max_kms}
                                                onChange={(e) => handleServiceRateChange(index, 'base_max_kms', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="0.00"
                                                required
                                            />
                                        </div>

                                        {/* Additional Rate per KM */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Additional Rate per KM ({localeConfig.currency.symbol}) *
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={rate.additional_rate_per_km}
                                                onChange={(e) => handleServiceRateChange(index, 'additional_rate_per_km', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="0.00"
                                                required
                                            />
                                        </div>

                                        {/* Fixed Rate */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Fixed Rate ({localeConfig.currency.symbol})
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={rate.fixed_rate}
                                                onChange={(e) => handleServiceRateChange(index, 'fixed_rate', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="0.00"
                                            />
                                        </div>

                                        {/* Cancel Charges - Upto Base KMs */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Cancel Charges - Upto Base KMs ({localeConfig.currency.symbol}) *
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={rate.cancel_charges_per_km_upto_base_kms}
                                                onChange={(e) => handleServiceRateChange(index, 'cancel_charges_per_km_upto_base_kms', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="0.00"
                                                required
                                            />
                                        </div>

                                        {/* Cancel Charges - Above Base KMs */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Cancel Charges - Above Base KMs ({localeConfig.currency.symbol}) *
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={rate.cancel_charges_per_km_above_base_kms}
                                                onChange={(e) => handleServiceRateChange(index, 'cancel_charges_per_km_above_base_kms', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="0.00"
                                                required
                                            />
                                        </div>

                                        {/* Effective From */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Effective From *
                                            </label>
                                            <input
                                                type="date"
                                                value={rate.effective_from}
                                                onChange={(e) => handleServiceRateChange(index, 'effective_from', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            />
                                        </div>

                                        {/* Effective To */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Effective To
                                            </label>
                                            <input
                                                type="date"
                                                value={rate.effective_to}
                                                onChange={(e) => handleServiceRateChange(index, 'effective_to', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Add Service Rate Button */}
                <button
                    type="button"
                    onClick={addServiceRate}
                    className="flex items-center space-x-2 px-3 py-1.5 text-sm text-blue-600 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Another Service Rate</span>
                </button>

                {/* Navigation Buttons */}
                <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={onPrevious}
                        disabled={loading}
                        className="flex items-center justify-center space-x-2 px-3 py-2 sm:py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        <span>Previous</span>
                    </button>

                    <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={loading}
                            className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span>Save & Exit</span>
                        </button>

                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center justify-center space-x-2 w-full sm:w-auto px-4 py-2 sm:py-1.5 text-sm text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                                backgroundColor: colors.primary
                            }}
                        >
                            <span>{loading ? 'Saving...' : 'Next'}</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default VendorServiceRatesForm; 